// frontend/components/POS/CheckoutModal.jsx
import React, { useState, useEffect } from 'react';
import { XMarkIcon, CreditCardIcon, BanknotesIcon, DevicePhoneMobileIcon, UserIcon, DocumentTextIcon, PrinterIcon } from '@heroicons/react/24/outline';
import PaymentMethods from './PaymentMethods';
import Modal from '../Common/Modal';
import { customerService } from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';

export default function CheckoutModal({ total, items, onClose, onConfirm }) {
  const { user } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState(total || 0);
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [notes, setNotes] = useState('');
  const [printReceipt, setPrintReceipt] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // Safely get items count
  const itemsCount = items?.length || 0;

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Search customers
  useEffect(() => {
    const searchCustomers = async () => {
      if (paymentMethod === 'credit' && customerSearch.length > 1) {
        setIsSearching(true);
        try {
          const response = await customerService.getCustomers({ search: customerSearch, limit: 10 });
          setCustomerResults(response.data || []);
        } catch (error) {
          console.error('Error searching customers:', error);
          setCustomerResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setCustomerResults([]);
      }
    };
    
    const delayDebounce = setTimeout(searchCustomers, 300);
    return () => clearTimeout(delayDebounce);
  }, [customerSearch, paymentMethod]);

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerId(customer.id);
    setCustomerSearch('');
    setCustomerResults([]);
  };

  const change = amountPaid - (total || 0);
  const isFullyPaid = amountPaid >= (total || 0);

  // Generate receipt HTML with business details from logged-in user
  const generateReceiptHTML = (transactionData) => {
    // Get business details from the authenticated user's tenant
    const businessName = user?.business_name || 'Brominon';
    const businessPhone = user?.phone || '+254 727 332 761';
    const businessEmail = user?.email || 'info@brominon.com';
    const businessAddress = user?.address || 'Nairobi, Kenya';
    const businessPin = user?.tin_number || 'N/A';
    
    const date = new Date().toLocaleString();
    const receiptNumber = `RCP-${Date.now()}`;
    const subtotal = total || 0;
    const vatAmount = subtotal * 0.16;
    const totalAmount = subtotal;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Receipt ${receiptNumber}</title>
        <style>
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            width: 300px;
            margin: 0 auto;
            padding: 10px;
          }
          .header {
            text-align: center;
            margin-bottom: 10px;
            border-bottom: 1px dashed #000;
            padding-bottom: 10px;
          }
          .business-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .business-details {
            font-size: 10px;
            margin-bottom: 3px;
          }
          .receipt-title {
            text-align: center;
            font-size: 14px;
            font-weight: bold;
            margin: 10px 0;
          }
          .divider {
            border-top: 1px dashed #000;
            margin: 10px 0;
          }
          .item-line {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            margin: 3px 0;
          }
          .totals {
            margin-top: 10px;
            border-top: 1px dashed #000;
            padding-top: 10px;
          }
          .total-line {
            display: flex;
            justify-content: space-between;
            font-weight: bold;
            margin: 5px 0;
          }
          .footer {
            text-align: center;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px dashed #000;
            font-size: 10px;
          }
          .thankyou {
            font-size: 12px;
            font-weight: bold;
            margin: 10px 0;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="business-name">${escapeHtml(businessName)}</div>
          <div class="business-details">${escapeHtml(businessAddress)}</div>
          <div class="business-details">Tel: ${escapeHtml(businessPhone)}</div>
          <div class="business-details">Email: ${escapeHtml(businessEmail)}</div>
          <div class="business-details">PIN: ${escapeHtml(businessPin)}</div>
        </div>
        
        <div class="receipt-title">SALES RECEIPT</div>
        
        <div class="divider"></div>
        
        <div>
          <div>Date: ${date}</div>
          <div>Receipt #: ${receiptNumber}</div>
          <div>Cashier: ${escapeHtml(transactionData.cashierName || user?.full_name || 'POS User')}</div>
          ${selectedCustomer ? `<div>Customer: ${escapeHtml(selectedCustomer.name)}</div>` : '<div>Customer: Walk-in Customer</div>'}
        </div>
        
        <div class="divider"></div>
        
        <div>
          <div class="item-line">
            <span>Item</span>
            <span>Qty</span>
            <span>Price</span>
            <span>Total</span>
          </div>
          ${items?.map(item => `
            <div class="item-line">
              <span>${escapeHtml(item.name.substring(0, 25))}</span>
              <span>${item.quantity}</span>
              <span>${item.price.toLocaleString()}</span>
              <span>${(item.price * item.quantity).toLocaleString()}</span>
            </div>
          `).join('')}
        </div>
        
        <div class="divider"></div>
        
        <div class="totals">
          <div class="total-line">
            <span>Subtotal:</span>
            <span>KES ${subtotal.toLocaleString()}</span>
          </div>
          <div class="total-line">
            <span>VAT (16%):</span>
            <span>KES ${vatAmount.toLocaleString()}</span>
          </div>
          <div class="total-line">
            <span>Total:</span>
            <span>KES ${totalAmount.toLocaleString()}</span>
          </div>
          <div class="total-line">
            <span>Amount Paid:</span>
            <span>KES ${amountPaid.toLocaleString()}</span>
          </div>
          ${paymentMethod === 'cash' && amountPaid > total ? `
            <div class="total-line">
              <span>Change:</span>
              <span>KES ${change.toLocaleString()}</span>
            </div>
          ` : ''}
          <div class="total-line">
            <span>Payment Method:</span>
            <span>${paymentMethod.toUpperCase()}</span>
          </div>
        </div>
        
        <div class="divider"></div>
        
        <div class="thankyou">
          Thank you for your business!
        </div>
        
        <div class="footer">
          <div>No refunds without original receipt</div>
          <div>Goods once sold cannot be returned</div>
          <div>** This is a computer generated receipt **</div>
        </div>
      </body>
      </html>
    `;
  };

  // Helper function to escape HTML
  const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Print receipt function
  const printReceiptFn = async (transactionData) => {
    console.log('🖨️ Print function called!', transactionData);
    
    setIsPrinting(true);
    
    try {
      const receiptHTML = generateReceiptHTML(transactionData);
      
      // Create a hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0px';
      iframe.style.height = '0px';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(receiptHTML);
      iframeDoc.close();
      
      iframe.contentWindow.onload = () => {
        iframe.contentWindow.print();
        
        setTimeout(() => {
          document.body.removeChild(iframe);
          setIsPrinting(false);
          toast.success('Receipt printed successfully!');
        }, 1000);
      };
      
      // Also save a copy automatically
      const blob = new Blob([receiptHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${Date.now()}.html`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('❌ Print error:', error);
      toast.error('Failed to print receipt');
      setIsPrinting(false);
    }
  };

 // In CheckoutModal.jsx - Update handleConfirm
const handleConfirm = async () => {
  if (isPrinting) return;
  
  setIsPrinting(true);
  
  try {
    const result = await onConfirm({
      method: paymentMethod,
      amount: amountPaid,
      customerId: customerId || null,
      customerName: selectedCustomer?.name,
      notes,
      printReceipt
    });
    
    // For M-Pesa, close modal immediately (no receipt printing needed)
    if (paymentMethod === 'mpesa') {
      onClose(); // Close modal immediately
      setIsPrinting(false);
      return;
    }
    
    // For other payment methods, handle receipt printing
    if (printReceipt && result && result.success) {
      await printReceiptFn({
        transactionId: result.transactionId,
        invoiceNumber: result.invoiceNumber,
        cashierName: result.cashierName,
        customerName: selectedCustomer?.name,
        total: total,
        amountPaid: amountPaid,
        paymentMethod: paymentMethod,
        items: items
      });
    }
    
    if (result && result.success) {
      onClose();
    }
  } catch (error) {
    console.error('Checkout error:', error);
    toast.error('Failed to complete sale');
  } finally {
    setIsPrinting(false);
  }
};

  return (
    <Modal isOpen={true} onClose={onClose} title="Checkout" size="large">
      <div className="space-y-4 sm:space-y-6">
        {/* Order Summary */}
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
          <h3 className="font-semibold text-sm sm:text-base mb-2">Order Summary</h3>
          <div className="space-y-1 text-xs sm:text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Items ({itemsCount}):</span>
              <span>KES {(total || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between font-bold pt-2 border-t">
              <span>Total:</span>
              <span className="text-blue-600">KES {(total || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
          <PaymentMethods selected={paymentMethod} onChange={setPaymentMethod} />
        </div>

        {/* Amount Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Amount Paid {paymentMethod !== 'credit' && <span className="text-red-500">*</span>}
          </label>
          <input
            type="number"
            step="0.01"
            value={amountPaid}
            onChange={(e) => setAmountPaid(parseFloat(e.target.value) || 0)}
            className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter amount"
            disabled={paymentMethod === 'credit'}
          />
          
          {/* Change and Balance Display */}
          <div className="mt-2 space-y-1">
            {paymentMethod === 'cash' && amountPaid > (total || 0) && (
              <div className="text-green-600 text-xs sm:text-sm flex justify-between items-center p-2 bg-green-50 rounded-lg">
                <span>Change to return:</span>
                <span className="font-semibold">KES {change.toFixed(2)}</span>
              </div>
            )}
            {amountPaid < (total || 0) && paymentMethod !== 'credit' && (
              <div className="text-red-600 text-xs sm:text-sm flex justify-between items-center p-2 bg-red-50 rounded-lg">
                <span>Balance due:</span>
                <span className="font-semibold">KES {((total || 0) - amountPaid).toFixed(2)}</span>
              </div>
            )}
            {paymentMethod === 'credit' && (
              <div className="text-yellow-600 text-xs sm:text-sm flex justify-between items-center p-2 bg-yellow-50 rounded-lg">
                <span>Amount to be added to customer balance:</span>
                <span className="font-semibold">KES {(total || 0).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Customer Selection for Credit Sales */}
        {paymentMethod === 'credit' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer <span className="text-red-500">*</span>
            </label>
            
            {!selectedCustomer ? (
              <div>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name or phone..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                {/* Search Results */}
                {customerSearch.length > 1 && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {isSearching ? (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : customerResults.length > 0 ? (
                      customerResults.map(customer => (
                        <button
                          key={customer.id}
                          onClick={() => selectCustomer(customer)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 transition-colors"
                        >
                          <div className="font-medium text-sm">{customer.name}</div>
                          <div className="text-xs text-gray-500 flex justify-between mt-0.5">
                            <span>{customer.phone || 'No phone'}</span>
                            {customer.current_balance > 0 && (
                              <span className="text-red-500">Balance: KES {customer.current_balance.toLocaleString()}</span>
                            )}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        No customers found. 
                        <button className="text-blue-600 ml-1">Add new customer</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-sm">{selectedCustomer.name}</div>
                  <div className="text-xs text-gray-600">{selectedCustomer.phone}</div>
                </div>
                <button
                  onClick={() => {
                    setSelectedCustomer(null);
                    setCustomerId('');
                  }}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Change
                </button>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea
            rows={isMobile ? 2 : 3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="Add any notes about this transaction..."
          />
        </div>

        {/* Print Receipt */}
        <label className="flex items-center space-x-2 cursor-pointer py-1">
          <input
            type="checkbox"
            checked={printReceipt}
            onChange={(e) => setPrintReceipt(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            <PrinterIcon className="h-4 w-4 inline mr-1" />
            Print receipt
          </span>
        </label>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-2 sm:pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2.5 sm:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={(paymentMethod === 'credit' ? !customerId : !isFullyPaid) || isPrinting}
            className="px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base font-medium"
          >
            {isPrinting ? 'Printing...' : (paymentMethod === 'credit' ? 'Create Credit Sale' : 'Complete Sale')}
          </button>
        </div>

        {/* Help Text for Mobile */}
        {isMobile && paymentMethod === 'cash' && amountPaid < (total || 0) && (
          <p className="text-xs text-gray-500 text-center">
            Enter the amount received from customer
          </p>
        )}
      </div>
    </Modal>
  );
}